import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

type UseMutationObserverRequest = {
  enabled: boolean;
  /** Observe this ref for children and assume it can be recreated if not yet  */
  evervizContainerRef: MutableRefObject<HTMLDivElement | null>;
  /** The maximum number of times we will insert/remove the script before stopping  */
  maxRetries: number;
  /**
   * Ref to the element where the script tag will be inserted.
   *
   * This is configurable for easier testing
   */
  scriptAnchorRef: MutableRefObject<HTMLElement | null>;
  /** url of the script */
  src: string;
  /** The time (in ms) after inserting a script before we evaluate the container and reinsert the script */
  retryTimeout: number;
};

/**
 * This hook is used to re-insert everviz scripts until we see detect it has successfully ran
 *
 * Everviz scripts have a race condition on some chart types when too many charts are rendered at once.
 * This occurs because the chart will initialize before all highchart modules loaded from the everviz script
 *  have successfully ran. This creates a JS runtime error on the module script.
 *
 * We have no way to catch the javascript error from highcharts, so we watch for activity in the
 * everviz container to know whether or not the script has successfully ran.
 */
export const useEvervizScriptInitialize = ({
  enabled,
  evervizContainerRef,
  maxRetries,
  retryTimeout,
  scriptAnchorRef,
  src,
}: UseMutationObserverRequest) => {
  /** Number of attempts we have inserted the script */
  const retries = useRef<number>(0);
  const [activeScript, setActiveScript] = useState<HTMLScriptElement | null>(
    null
  );

  useEffect(() => {
    if (enabled && !activeScript && scriptAnchorRef.current) {
      const script = document.createElement("script");
      script.src = src;
      script.defer = true;
      scriptAnchorRef.current.appendChild(script);

      // set the script, triggering the other useEffect
      setActiveScript(script);
    }
  }, [
    activeScript,
    enabled,
    evervizContainerRef,
    maxRetries,
    retryTimeout,
    scriptAnchorRef,
    src,
  ]);

  // once the active script has been set, set a timeout to
  // evaluate the container and clear the active script
  useEffect(() => {
    if (enabled && activeScript) {
      const observer = new MutationObserver(() => {
        // any time the mutation observer sees a change to the child
        // elements on the container, the script has successfully ran
        observer.disconnect();
      });

      observer.observe(evervizContainerRef.current!, {
        childList: true,
        subtree: false,
        attributes: false,
      });

      const token = setTimeout(() => {
        if (
          !evervizContainerRef.current?.childNodes.length &&
          retries.current < maxRetries
        ) {
          // remove the old script and trigger
          // the first useEffect by clearing activeScript
          retries.current += 1;
          activeScript.remove();
          setActiveScript(null);
        }
      }, retryTimeout);

      return () => {
        clearTimeout(token);
        observer.disconnect();
      };
    }

    return () => {};
  }, [activeScript, enabled, evervizContainerRef, maxRetries, retryTimeout]);

  const forceRetry = useCallback(() => {
    if (activeScript) {
      activeScript.remove();
      setActiveScript(null);
    }
  }, [activeScript]);

  return {
    forceRetry,
  };
};
